import { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { 
  Users, 
  MessageSquare, 
  Calendar, 
  Eye,
  TrendingUp,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { currentTheme } = useTheme();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/analytics`, { withCredentials: true });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = analytics ? [
    { 
      label: 'Total Users', 
      value: analytics.total_users, 
      icon: Users, 
      color: currentTheme.primary,
      change: analytics.new_users_week,
      changeLabel: 'new this week'
    },
    { 
      label: 'Total Messages', 
      value: analytics.total_messages, 
      icon: MessageSquare, 
      color: currentTheme.secondary,
      change: analytics.unread_messages,
      changeLabel: 'unread'
    },
    { 
      label: 'Coaching Sessions', 
      value: analytics.total_sessions, 
      icon: Calendar, 
      color: currentTheme.accent,
      change: analytics.pending_sessions,
      changeLabel: 'pending'
    },
    { 
      label: 'Page Views', 
      value: analytics.visitor_stats?.reduce((acc, v) => acc + (v.visits || 0), 0) || 0, 
      icon: Eye, 
      color: '#E07A5F',
      change: null,
      changeLabel: 'last 30 days'
    },
  ] : [];

  return (
    <AdminLayout title="Dashboard">
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="skeleton h-32 rounded-xl" />
          ))}
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="stat-card"
                data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${stat.color}20` }}
                  >
                    <stat.icon className="w-6 h-6" style={{ color: stat.color }} />
                  </div>
                  {stat.change !== null && (
                    <span 
                      className="flex items-center gap-1 text-sm font-medium"
                      style={{ color: stat.color }}
                    >
                      <TrendingUp className="w-4 h-4" />
                      +{stat.change}
                    </span>
                  )}
                </div>
                <div className="font-display text-3xl font-bold mb-1" style={{ color: currentTheme.foreground }}>
                  {stat.value.toLocaleString()}
                </div>
                <div className="font-body text-sm opacity-60">
                  {stat.label}
                  {stat.changeLabel && <span className="ml-1">• {stat.changeLabel}</span>}
                </div>
              </div>
            ))}
          </div>

          {/* Visitor Chart */}
          <div 
            className="rounded-2xl p-6"
            style={{ backgroundColor: 'white', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}
          >
            <h2 className="font-display text-xl font-medium mb-6" style={{ color: currentTheme.foreground }}>
              Visitor Statistics (Last 30 Days)
            </h2>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={analytics?.visitor_stats || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke={currentTheme.border} />
                  <XAxis 
                    dataKey="date" 
                    stroke={currentTheme.foreground}
                    opacity={0.5}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis 
                    stroke={currentTheme.foreground}
                    opacity={0.5}
                    tick={{ fontSize: 12 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white',
                      border: `1px solid ${currentTheme.border}`,
                      borderRadius: '8px'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="visits" 
                    stroke={currentTheme.primary} 
                    strokeWidth={2}
                    dot={{ fill: currentTheme.primary }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
